import type { Deck, DeckSlot } from "@punkrecords/types";
import { createEmptyDeck } from "./storage.js";

const CARD_ID = String.raw`[A-Za-z]{1,4}\d{0,2}-\d{3}`;
const LIMITLESS_SECTION_RE = /^(Leader|Character|Event|Stage)\b/i;
const LIMITLESS_CARD_LINE_RE = new RegExp(
  String.raw`^(\d+)\s+.+\((${CARD_ID})\)\s*$`,
  "i",
);

type LimitlessSection = "leader" | "character" | "event" | "stage" | null;

/**
 * True when text looks like a Limitless tournament deck export:
 * section headers (Leader / Character / Event / Stage) plus
 * `{count} {name} ({cardId})` card lines.
 */
export function isLimitlessDeckText(text: string): boolean {
  let hasSection = false;
  let hasCardLine = false;
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith("//")) continue;
    if (LIMITLESS_SECTION_RE.test(line)) hasSection = true;
    if (LIMITLESS_CARD_LINE_RE.test(line)) hasCardLine = true;
  }
  return hasSection && hasCardLine;
}

/**
 * Parse a Limitless tournament deck list into a Deck.
 *
 * Format:
 *   Leader
 *   1 Enel (OP15-058)
 *
 *   Character (29)
 *   4 Charlotte Pudding (OP12-071)
 *
 *   Event (21)
 *   4 El Thor (OP15-075)
 */
export function limitlessTextToDeck(text: string): Deck {
  const deck = createEmptyDeck();
  let section: LimitlessSection = null;

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;

    if (line.startsWith("//")) {
      if (line.startsWith("// ") && deck.name === "New Deck") {
        deck.name = line.slice(3).trim();
      }
      continue;
    }

    const sectionMatch = line.match(/^(Leader|Character|Event|Stage)\b/i);
    if (sectionMatch) {
      section = sectionMatch[1].toLowerCase() as LimitlessSection;
      continue;
    }

    const cardMatch = line.match(LIMITLESS_CARD_LINE_RE);
    if (!cardMatch) continue;

    const count = parseInt(cardMatch[1], 10);
    const cardId = cardMatch[2].toUpperCase();
    if (!Number.isFinite(count) || count <= 0) continue;

    if (section === "leader") {
      deck.leader = cardId;
      continue;
    }

    addSlot(deck.cards, cardId, count);
  }

  return deck;
}

/**
 * Serialize a deck to a human-readable text list.
 *
 * Format:
 *   Leader: OP01-001
 *   4 OP01-025
 *   3 OP01-051
 */
export function deckToText(
  deck: Deck,
  cardNameLookup?: (cardId: string) => string | undefined,
): string {
  const lines: string[] = [];

  if (deck.name) {
    lines.push(`// ${deck.name}`);
  }

  if (deck.leader) {
    const name = cardNameLookup?.(deck.leader);
    lines.push(name ? `Leader: ${deck.leader} ${name}` : `Leader: ${deck.leader}`);
  }

  for (const slot of deck.cards) {
    const name = cardNameLookup?.(slot.cardId);
    lines.push(name ? `${slot.count} ${slot.cardId} ${name}` : `${slot.count} ${slot.cardId}`);
  }

  return lines.join("\n");
}

/**
 * Parse a text list into a Deck.
 *
 * Accepted formats per line:
 *   Leader: OP01-001          — explicit leader
 *   L: OP01-001               — explicit leader (short)
 *   4 OP01-025                — count then card
 *   4x OP01-025               — count-x then card (space)
 *   4xOP01-025                — count-x then card (no space, SIM format)
 *   OP01-025 x4               — card then count
 *   OP01-025                  — bare card (count = 1)
 *   // comment                — ignored (first comment becomes deck name)
 *   empty lines               — ignored
 *
 * SIM compatibility: when no explicit "Leader:" line is present, the
 * first card in the list is treated as the leader (SIM convention).
 *
 * Limitless compatibility: tournament exports with section headers and
 * `{count} Name (CARD-ID)` lines are auto-detected and parsed separately.
 */
export function textToDeck(text: string): Deck {
  if (isLimitlessDeckText(text)) {
    return limitlessTextToDeck(text);
  }

  const deck = createEmptyDeck();
  const lines = text.split(/\r?\n/);
  let hasExplicitLeader = false;
  const cardOrder: string[] = [];

  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith("//")) {
      if (line.startsWith("// ") && deck.name === "New Deck") {
        deck.name = line.slice(3).trim();
      }
      continue;
    }

    const leaderMatch = line.match(/^(?:leader|l)\s*:\s*([A-Za-z]{1,4}\d{0,2}-\d{3})/i);
    if (leaderMatch) {
      deck.leader = leaderMatch[1].toUpperCase();
      hasExplicitLeader = true;
      continue;
    }

    const negativeMatch = line.match(/^-(\d+)\s*x?\s*([A-Za-z]{1,4}\d{0,2}-\d{3})/i);
    if (negativeMatch) {
      const cardId = negativeMatch[2].toUpperCase();
      deck.leader = cardId;
      hasExplicitLeader = true;
      continue;
    }

    const countFirstMatch = line.match(/^(\d+)\s*x?\s*([A-Za-z]{1,4}\d{0,2}-\d{3})/i);
    if (countFirstMatch) {
      const count = parseInt(countFirstMatch[1], 10);
      const cardId = countFirstMatch[2].toUpperCase();
      if (!cardOrder.includes(cardId)) cardOrder.push(cardId);
      addSlot(deck.cards, cardId, count);
      continue;
    }

    const countLastMatch = line.match(/^([A-Za-z]{1,4}\d{0,2}-\d{3})\s*x?\s*(\d+)/i);
    if (countLastMatch) {
      const cardId = countLastMatch[1].toUpperCase();
      const count = parseInt(countLastMatch[2], 10);
      if (!cardOrder.includes(cardId)) cardOrder.push(cardId);
      addSlot(deck.cards, cardId, count);
      continue;
    }

    const bareMatch = line.match(/^([A-Za-z]{1,4}\d{0,2}-\d{3})/i);
    if (bareMatch) {
      const cardId = bareMatch[1].toUpperCase();
      if (!cardOrder.includes(cardId)) cardOrder.push(cardId);
      addSlot(deck.cards, cardId, 1);
    }
  }

  if (!hasExplicitLeader && !deck.leader && cardOrder.length > 0) {
    const firstCardId = cardOrder[0];
    deck.leader = firstCardId;
    deck.cards = deck.cards.filter((s: { cardId: string }) => s.cardId !== firstCardId);
  }

  return deck;
}

function addSlot(cards: DeckSlot[], cardId: string, count: number): void {
  const existing = cards.find((s) => s.cardId === cardId);
  if (existing) {
    existing.count += count;
  } else {
    cards.push({ cardId, count });
  }
}

/**
 * Serialize a deck to SIM-compatible format.
 *
 * Format:
 *   1xST18-001
 *   4xOP12-070
 *   3xOP03-044
 *
 * Leader is included as a regular line (SIM doesn't distinguish).
 */
export function deckToSim(deck: Deck): string {
  const lines: string[] = [];

  if (deck.leader) {
    lines.push(`1x${deck.leader}`);
  }

  for (const slot of deck.cards) {
    lines.push(`${slot.count}x${slot.cardId}`);
  }

  return lines.join("\n");
}

/**
 * Encode a deck into a compact URL-safe string for sharing.
 * Format: leader|cardId:count,cardId:count,...
 * Then base64url-encoded.
 */
export function deckToUrlParam(deck: Deck): string {
  const leader = deck.leader ?? "";
  const cardParts = deck.cards.map((s: { cardId: string; count: number }) => `${s.cardId}:${s.count}`);
  const payload = [leader, ...cardParts].join("|");
  return btoa(payload).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Decode a URL param back into a Deck.
 */
export function urlParamToDeck(param: string): Deck {
  const padded = param.replace(/-/g, "+").replace(/_/g, "/");
  const decoded = atob(padded);
  const parts = decoded.split("|");

  const deck = createEmptyDeck();
  const leader = (parts[0] ?? "").trim().toUpperCase();
  if (leader && /^[A-Z]{2}\d{2}-\d{3}$/.test(leader)) {
    deck.leader = leader;
  }

  for (let i = 1; i < parts.length; i++) {
    const [cardId, countStr] = parts[i].split(":");
    if (!cardId) continue;
    const count = parseInt(countStr ?? "1", 10);
    deck.cards.push({
      cardId: cardId.toUpperCase(),
      count: Number.isFinite(count) && count > 0 ? count : 1,
    });
  }

  return deck;
}
